import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { Button, PageHeader, SectionCard } from '@/components/ui';
import { ActingOrgPicker } from '@/components/ActingOrgPicker';
import { useConsoleBreadcrumb } from '@/components/shell/ConsoleBreadcrumbContext';
import { fetchPermissionCatalog } from '@/api/orgAdmin';
import { PermissionCatalogTab } from './Permissions/PermissionCatalogTab';
import { DownloadIcon } from './Permissions/icons';
import { RolesTab } from './Permissions/RolesTab';
import { UserGroupsTab } from './Permissions/UserGroupsTab';

type TabKey = 'catalog' | 'roles' | 'groups';

/**
 * EAA-1842: `/console/permissions` — "Phân quyền", rebuilt to the three-tab design
 * (`design_handoff_portal_role_mapping/exports/permissions/01..03`).
 *
 * ONLY TAB 1 IS REAL, and the design says so itself: frames 01-03 label "Vai trò"
 * and "Nhóm người dùng" with a `cần API mới` badge. That is not a shortcut on our
 * side — portal has no console-role model at all (`super_admin` is a
 * `ConsoleRoleGrant` row, `org_admin` is an `OrganizationAdmin` grant), and no
 * user-group model whatsoever. Building either tab against invented client state
 * would produce a screen that appears to save and does not.
 *
 * So the two future tabs are reachable, visibly badged, and explain exactly which
 * backend piece is missing. That is more useful than hiding them: it tells an
 * operator the capability is planned rather than broken, and it keeps the design's
 * information architecture intact for whoever builds the next phase.
 *
 * Tab state is URL-backed (`?tab=`) so a link survives reload, matching the
 * `?view=` / `?source=` convention on the role-map and audit screens.
 *
 * Gating: `console.permissions.view`, held by BOTH built-in roles — understanding
 * your own ceiling is what makes a 403 actionable. Gate covers render AND fetch.
 */
export function PermissionsPage() {
    const { t } = useTranslation();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const allowed = can('console.permissions.view');

    // EAA-1845: built inside the component (not module scope) so the labels can
    // go through t() — TAB_LABELS/SUBTITLES used to be plain module constants.
    const TAB_LABELS: Record<TabKey, string> = {
        catalog: t('console.permissions.page.tabLabels.catalog'),
        roles: t('console.permissions.page.tabLabels.roles'),
        groups: t('console.permissions.page.tabLabels.groups'),
    };

    /** Each frame writes its own lede; the tabs are three different jobs. */
    const SUBTITLES: Record<TabKey, string> = {
        catalog: t('console.permissions.page.subtitles.catalog'),
        roles: t('console.permissions.page.subtitles.roles'),
        groups: t('console.permissions.page.subtitles.groups'),
    };

    // The CSV builder lives in the tab (it owns the filtered rows) but the button
    // lives in the page header, so the tab publishes its exporter through this ref.
    // A ref rather than lifted state on purpose: the export is an imperative action,
    // and lifting the filter state up would re-render the whole page on every
    // keystroke in the tab's search box.
    const csvRef = useRef<(() => void) | null>(null);

    // EAA-1843 (frame 04): "+ Tạo nhóm" is drawn in the page-header band, but the
    // modal and its form state belong to the groups tab. Same ref hand-off as the
    // CSV button above — the header gets a trigger, the tab keeps ownership, and
    // nothing about the group form has to be lifted into this component.
    const createGroupRef = useRef<(() => void) | null>(null);

    const rawTab = searchParams.get('tab');
    const tab: TabKey =
        rawTab === 'roles' || rawTab === 'groups' ? (rawTab as TabKey) : 'catalog';

    const catalogQuery = useQuery({
        queryKey: ['permission-catalog', accessToken],
        queryFn: () => fetchPermissionCatalog(config.apiBase, accessToken as string),
        enabled: allowed && authenticated && Boolean(accessToken),
        retry: false,
    });

    // EAA-1846 (frame 95): breadcrumb moved to the shared topbar. Called
    // unconditionally (Rules of Hooks) — ahead of the capability early return
    // below, since a hook cannot follow one. Frames 02-04 carry the tab as a
    // third crumb; on the default tab the frame stops at "Phân quyền", so a
    // third crumb repeating the page title is omitted.
    useConsoleBreadcrumb(
        tab === 'catalog'
            ? [
                  { label: t('console.permissions.page.breadcrumbParent') },
                  { label: t('console.permissions.page.title') },
              ]
            : [
                  { label: t('console.permissions.page.breadcrumbParent') },
                  { label: t('console.permissions.page.title') },
                  { label: TAB_LABELS[tab] },
              ]
    );

    // §1b render gate: without the capability this renders nothing, and the query
    // above never ran either.
    if (!allowed) return null;

    function selectTab(next: TabKey) {
        const params = new URLSearchParams(searchParams);
        if (next === 'catalog') params.delete('tab');
        else params.set('tab', next);
        setSearchParams(params, { replace: true });
    }

    return (
        <main className="portal-admin portal-console-page portal-console-page--fit">
            {/* EAA-1842: the CSV action goes through PageHeader's own actions slot
                rather than bespoke markup. */}
            <PageHeader
                title={t('console.permissions.page.title')}
                subtitle={SUBTITLES[tab]}
                // EAA-1804: raw `<button className="portal-btn[...]">` -> kit `Button`.
                // `perm-csv` carries real layout CSS and is kept as `className`; the
                // create-group action already had `-primary` so it becomes
                // `type="primary"`. Neither sits in a `.portal-admin-form`.
                actions={
                    tab === 'catalog' ? (
                        <Button className="perm-csv" onClick={() => csvRef.current?.()}>
                            <DownloadIcon />
                            {t('console.permissions.page.downloadCsv')}
                        </Button>
                    ) : tab === 'groups' ? (
                        <>
                            {/* Groups are org-owned, so frame 04 pins the scope
                                next to the actions rather than leaving it to be
                                inferred from the sidebar. */}
                            <ActingOrgPicker variant="compact" />
                            {can('console.groups.manage') && (
                                <Button
                                    type="primary"
                                    onClick={() => createGroupRef.current?.()}
                                >
                                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none"
                                         stroke="currentColor" strokeWidth={2} aria-hidden="true">
                                        <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                                    </svg>
                                    {t('console.permissions.page.createGroup')}
                                </Button>
                            )}
                        </>
                    ) : undefined
                }
            />

            <div className="perm-tabs" role="tablist" aria-label={t('console.permissions.page.title')}>
                <TabButton
                    active={tab === 'catalog'}
                    onSelect={() => selectTab('catalog')}
                    label={TAB_LABELS.catalog}
                />
                <TabButton
                    active={tab === 'roles'}
                    onSelect={() => selectTab('roles')}
                    label={TAB_LABELS.roles}
                />
                <TabButton
                    active={tab === 'groups'}
                    onSelect={() => selectTab('groups')}
                    label={TAB_LABELS.groups}
                />
            </div>

            {tab === 'catalog' && (
                <>
                    {/* Frame 62 drops the "danh mục chỉ đọc" callout, and it has
                        to go: the matrix EDITS the role map, so a note saying
                        this screen cannot change anything is now simply false.
                        What survives of it — that the two built-ins are locked —
                        moved into the page lede, where it is one clause instead
                        of a band. */}
                    {/* Frame 62 also drops the "Quyền của bạn" card: the matrix
                        plus its save bar fill the region, and a second card below
                        it would push the grid's own scroll shorter than the fit
                        rule allows. The same fact is one click away — the caller's
                        roles are columns in this very table. */}
                    <div className="fit-grow">
                        <SectionCard bodyPadded={false}>
                            <PermissionCatalogTab query={catalogQuery} exportRef={csvRef} />
                        </SectionCard>
                    </div>
                </>
            )}

            {/* No SectionCard here: frame 63 draws TWO cards side by side (the
                role rail and the editor pane), each with its own ring. Wrapping
                them in a third would put a card inside a card. */}
            {tab === 'roles' && (
                <div className="fit-grow perm-bare">
                    <RolesTab catalog={catalogQuery} />
                </div>
            )}

            {tab === 'groups' && (
                /* The roster note that used to sit here as a permanent band is
                   gone. It explained two things — members are Keycloak `sub`s,
                   and "Sửa danh sách" REPLACES the list — and the modal that
                   does both already says both, at the moment it matters. A
                   banner repeating it cost ~70px on every visit to tell most
                   readers something they were not about to do. */
                <div className="fit-grow">
                    <SectionCard>
                        <UserGroupsTab createRef={createGroupRef} />
                    </SectionCard>
                </div>
            )}
        </main>
    );
}

interface TabButtonProps {
    active: boolean;
    onSelect: () => void;
    label: string;
}

function TabButton({ active, onSelect, label }: TabButtonProps) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            className={active ? 'perm-tab perm-tab--on' : 'perm-tab'}
            onClick={onSelect}
        >
            {label}
        </button>
    );
}

export default PermissionsPage;
